/** Wer ist angemeldet. Eine Quelle fuer die ganze Oberflaeche. */
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, sitzungsendeMelden, type Sitzungsauskunft } from './api'

interface Anmeldelage {
  auskunft: Sitzungsauskunft | null
  laedt: boolean
  /** Wahr, wenn die Sitzung waehrend der Arbeit abgelaufen ist. */
  abgelaufen: boolean
  anmelden: (email: string, passwort: string) => Promise<void>
  abmelden: () => Promise<void>
}

const Zusammenhang = createContext<Anmeldelage | null>(null)

export function AnmeldungAnbieter({ children }: { children: ReactNode }) {
  const [auskunft, setzeAuskunft] = useState<Sitzungsauskunft | null>(null)
  const [laedt, setzeLaedt] = useState(true)
  const [abgelaufen, setzeAbgelaufen] = useState(false)
  const abfragen = useQueryClient()

  // Antwortet irgendeine Abfrage mit 401, ist die Sitzung vorbei: Anmeldelage
  // zuruecksetzen, Zwischenspeicher leeren (die Daten gehoeren der alten
  // Sitzung), und die Anmeldung zeigen statt einer Ansicht voller Fehler.
  useEffect(() => {
    sitzungsendeMelden(() => {
      setzeAuskunft((vorher) => {
        if (vorher) {
          setzeAbgelaufen(true)
          abfragen.clear()
        }
        return null
      })
    })
    return () => sitzungsendeMelden(null)
  }, [abfragen])

  useEffect(() => {
    let abgebrochen = false
    void (async () => {
      try {
        const gelesen = await api.holen<Sitzungsauskunft>('/api/ich')
        if (!abgebrochen) setzeAuskunft(gelesen)
      } catch (fehler) {
        // 401 ist der Normalfall beim ersten Aufruf, kein Grund zur Aufregung.
        if (!abgebrochen && !(fehler instanceof ApiFehler && fehler.status === 401)) {
          console.error(fehler)
        }
      } finally {
        if (!abgebrochen) setzeLaedt(false)
      }
    })()
    return () => {
      abgebrochen = true
    }
  }, [])

  const anmelden = useCallback(async (email: string, passwort: string) => {
    await api.senden('/api/anmeldung', { email, passwort })
    setzeAuskunft(await api.holen<Sitzungsauskunft>('/api/ich'))
    setzeAbgelaufen(false)
  }, [])

  const abmelden = useCallback(async () => {
    try {
      await api.senden('/api/abmeldung')
    } finally {
      setzeAuskunft(null)
      setzeAbgelaufen(false)
      abfragen.clear()
    }
  }, [abfragen])

  return <Zusammenhang.Provider value={{ auskunft, laedt, abgelaufen, anmelden, abmelden }}>{children}</Zusammenhang.Provider>
}

export function useAnmeldung(): Anmeldelage {
  const lage = useContext(Zusammenhang)
  if (!lage) throw new Error('useAnmeldung braucht den AnmeldungAnbieter darum herum.')
  return lage
}
